import { getInsertionsWithPendingChanges } from '@paragon/layout-models';

import { pickSingleActiveBuildingGuid } from '../../library/hierarchy-filtering/active-building';
import { pickSingleActiveLevelGuid } from '../../library/hierarchy-filtering/active-level';
import { VisibilityConfig } from '../../library/hierarchy-filtering/visible-objects';
import { getAllBuildingGuids, getAllLevelGuids } from '../selectors/layoutDataSelectors';
import { __UNSAFE_layoutHierarchyVisibilityAltered } from '../slices/selectionsSlice';
import { ThunkActionVoid } from './thunkActions';

function getBuildingGuidForLevel(levelGuid: string, buildingGuids: Set<string>, insertions: any[]): string | null {
  const insertion = insertions.find(
    (insertion) => buildingGuids.has(insertion.parentGuid) && insertion.objectGuid === levelGuid,
  );

  return insertion ? insertion.parentGuid : null;
}

export const ensureLevelIsVisible =
  (
    levelGuid: string,
    options?: {
      /** Ensure this level is the active level. Defaults to `false`. */
      makeActive?: boolean;

      /** Ensure this is the only visible level. Defaults to `false`. */
      exclusive?: boolean;
    },
  ): ThunkActionVoid =>
  (dispatch, getState) => {
    const state = getState();

    const buildingGuids = getAllBuildingGuids(state);
    const insertions = getInsertionsWithPendingChanges(state);

    const activeBuildingGuid = getBuildingGuidForLevel(levelGuid, buildingGuids, insertions);
    if (activeBuildingGuid == null) {
      throw new Error('Unable to determine building guid for targeted level');
    }

    if (state.selections.layoutHierarchyFilter === 'ghost') {
      dispatch(
        __UNSAFE_layoutHierarchyVisibilityAltered({
          activeBuildingGuid,
          activeLevelGuid: levelGuid,
          visibleLevels: VisibilityConfig.only(levelGuid),
        }),
      );

      return;
    }

    const currentFilter = state.selections.layoutHierarchyFilter;

    const changingBuilding = currentFilter === null || activeBuildingGuid !== currentFilter.activeBuildingGuid;
    const setLevelAsActive = changingBuilding || options?.makeActive;

    const payload = {
      activeBuildingGuid: changingBuilding ? activeBuildingGuid : currentFilter.activeBuildingGuid,
      activeLevelGuid: setLevelAsActive || options?.exclusive ? levelGuid : currentFilter.activeLevelGuid,

      visibleLevels:
        options?.exclusive || changingBuilding
          ? VisibilityConfig.only(levelGuid)
          : VisibilityConfig.add(currentFilter.visibleLevels, levelGuid),
    };

    dispatch(__UNSAFE_layoutHierarchyVisibilityAltered(payload));
  };

export const hideLevelByGuid =
  (levelGuid: string): ThunkActionVoid =>
  (dispatch, getState) => {
    const state = getState();

    const currentFilter = state.selections.layoutHierarchyFilter;
    if (currentFilter === null || currentFilter === 'ghost') {
      throw new Error('Attempt to modify layout hierarchy when there is no active building.');
    }

    // by default we retain the active level guid...
    let { activeBuildingGuid, activeLevelGuid } = currentFilter; // eslint-disable-line prefer-const

    const newVisibleLevelConfiguration = VisibilityConfig.remove(currentFilter.visibleLevels, levelGuid);

    // ...unless it has been hidden, then we need to pick a new active level if possible
    if (levelGuid === activeLevelGuid) {
      const insertions = getInsertionsWithPendingChanges(state);
      const { activeBuildingGuid } = currentFilter;

      const allLevelsInActiveBuilding = insertions
        .filter((insertion) => insertion.parentGuid === activeBuildingGuid)
        .map((insertion) => insertion.objectGuid)
        .filter((levelInBuilding) => levelInBuilding !== levelGuid);

      const visibleLevelsInActiveBuilding = allLevelsInActiveBuilding.filter((guid) =>
        VisibilityConfig.shouldBeVisible(guid, newVisibleLevelConfiguration),
      );

      activeLevelGuid =
        pickSingleActiveLevelGuid(visibleLevelsInActiveBuilding) ??
        pickSingleActiveLevelGuid(allLevelsInActiveBuilding);
    }

    dispatch(
      __UNSAFE_layoutHierarchyVisibilityAltered({
        activeBuildingGuid,
        activeLevelGuid,
        visibleLevels: newVisibleLevelConfiguration,
      }),
    );
  };

export const showEntireBuilding = (): ThunkActionVoid => (dispatch, getState) => {
  const state = getState();

  const currentFilter = state.selections.layoutHierarchyFilter;

  const allBuildingGuids = getAllBuildingGuids(state);
  const allLevelGuids = getAllLevelGuids(state);

  const activeBuildingGuid =
    currentFilter && currentFilter !== 'ghost' && currentFilter.activeBuildingGuid !== null
      ? currentFilter.activeBuildingGuid
      : pickSingleActiveBuildingGuid(Array.from(allBuildingGuids));

  if (activeBuildingGuid == null) {
    throw new Error('Unable to determine active building when `showEntireBuilding` was called.');
  }

  const insertions = getInsertionsWithPendingChanges(state);

  const allLevelsInBuilding = insertions
    .filter((insertion) => insertion.parentGuid === activeBuildingGuid)
    .filter((insertion) => allLevelGuids.has(insertion.objectGuid))
    .map((insertion) => insertion.objectGuid);

  const activeLevelGuid =
    currentFilter &&
    currentFilter !== 'ghost' &&
    currentFilter.activeLevelGuid !== null &&
    allLevelsInBuilding.includes(currentFilter.activeLevelGuid)
      ? currentFilter.activeLevelGuid
      : pickSingleActiveLevelGuid(allLevelsInBuilding);

  dispatch(
    __UNSAFE_layoutHierarchyVisibilityAltered({
      activeBuildingGuid,
      activeLevelGuid,
      visibleLevels: VisibilityConfig.only(allLevelsInBuilding),
    }),
  );
};
